import { onlineManager, useIsFetching } from '@tanstack/react-query'
import { useEffect, useState, useSyncExternalStore } from 'react'

const SHOW_DELAY_MS = 400

function useIsOnline(): boolean {
  return useSyncExternalStore(
    cb => onlineManager.subscribe(cb),
    () => onlineManager.isOnline(),
    () => true,
  )
}

/**
 * True while an unscoped React Query fetch has been in flight for longer than
 * SHOW_DELAY_MS and the app believes it is online.
 *
 * Lives apart from BackgroundSyncIndicator so that file exports a component
 * and nothing else — react-refresh/only-export-components. It also keeps the
 * decision ("is anything syncing?") separable from the presentation, which is
 * what let the presentation drop from a 33px text banner to a 2px hairline
 * without touching this logic at all.
 */
export function useIsBackgroundSyncing(): boolean {
  const fetchingCount = useIsFetching()
  const isOnline = useIsOnline()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Offline means nothing can actually be syncing; suppress the indicator
    // even if a query is mid-flight (request in limbo until timeout).
    if (!isOnline || fetchingCount === 0) {
      setVisible(false)
      return
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [fetchingCount, isOnline])

  return visible
}
